import type { ProgressPoint } from '@yui-receipt/shared'

type Props = {
  points: ProgressPoint[]
  /** y-axis max — 10 for VAS, user-set for other metrics. */
  yMax?: number
  /** Unit label (VAS: 'คะแนน'). */
  unit?: string
  className?: string
}

/**
 * Minimal SVG trend line for a progress metric. No recharts dep — we only
 * ever plot one line with ≤20 points, so the overhead isn't worth it. The
 * component is responsive (viewBox + preserveAspectRatio) and renders a
 * skip message when there's <2 points (can't draw a line from one dot).
 */
export default function VasChart({
  points,
  yMax = 10,
  unit = '',
  className,
}: Props) {
  if (!points || points.length === 0) {
    return (
      <div className={className ?? 'card p-4 text-center text-sm text-slate-500'}>
        ยังไม่มีข้อมูลแนวโน้ม
      </div>
    )
  }

  // Fixed SVG coord system — scales down via CSS.
  const W = 320
  const H = 140
  const PAD_L = 28
  const PAD_R = 12
  const PAD_T = 12
  const PAD_B = 24
  const innerW = W - PAD_L - PAD_R
  const innerH = H - PAD_T - PAD_B

  const n = points.length
  const xStep = n > 1 ? innerW / (n - 1) : 0

  const toX = (i: number) => PAD_L + i * xStep
  const toY = (v: number) => PAD_T + innerH - (Math.max(0, Math.min(yMax, v)) / yMax) * innerH

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(p.value).toFixed(1)}`)
    .join(' ')

  const gridYs = [0, Math.round(yMax / 2), yMax]

  return (
    <div className={className ?? 'card p-3'}>
      <svg
        role="img"
        aria-label={`กราฟแนวโน้ม ${unit} ${points.length} ครั้ง`}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-40"
      >
        {gridYs.map((g) => (
          <g key={g}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={toY(g)}
              y2={toY(g)}
              stroke="#e2e8f0"
              strokeDasharray={g === 0 ? '0' : '3 3'}
            />
            <text
              x={PAD_L - 4}
              y={toY(g) + 3}
              textAnchor="end"
              fontSize="9"
              fill="#64748b"
            >
              {g}
            </text>
          </g>
        ))}

        {n > 1 && (
          <path d={pathD} fill="none" stroke="#2a56b0" strokeWidth="2" strokeLinejoin="round" />
        )}

        {points.map((p, i) => (
          <g key={i}>
            <circle cx={toX(i)} cy={toY(p.value)} r="3.5" fill="#2a56b0" />
            <text
              x={toX(i)}
              y={toY(p.value) - 7}
              textAnchor="middle"
              fontSize="9"
              fill="#334155"
            >
              {p.value}
            </text>
          </g>
        ))}

        {/* x-axis visit-date labels — only first/middle/last to avoid crowding */}
        {[0, Math.floor((n - 1) / 2), n - 1]
          .filter((v, i, a) => a.indexOf(v) === i)
          .map((i) => (
            <text
              key={`d-${i}`}
              x={toX(i)}
              y={H - 6}
              textAnchor="middle"
              fontSize="9"
              fill="#64748b"
            >
              {points[i].visitDate?.slice(5) /* MM-DD */}
            </text>
          ))}
      </svg>
      {unit && <div className="text-xs text-slate-500 text-right">หน่วย: {unit}</div>}
    </div>
  )
}
